import { useState, useEffect, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import api from "../services/api";
import "../styles/paymentManagement.css";
import { QRCodeCanvas } from "qrcode.react";

export default function PaymentManagement() {
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // QR / Online payment modal
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [qrPayment, setQrPayment] = useState(null);

  // Offline payment modal
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [offlineUser, setOfflineUser] = useState(null);
  const [offlineAmount, setOfflineAmount] = useState("");
  const [offlineDescription, setOfflineDescription] = useState("");
  const [offlinePaymentMethod, setOfflinePaymentMethod] = useState("CASH");
  const [offlineCreating, setOfflineCreating] = useState(false);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, paymentsRes] = await Promise.all([
        api.get("/users/basic"),
        api.get("/payments"),
      ]);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setPayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await loadData();
    setRefreshTrigger(prev => prev + 1);
  };

  // Users who have NEVER had any payment (one‑time rule)
  const eligibleUsers = useMemo(() => {
    const usersWithAnyPayment = new Set();
    payments.forEach(p => usersWithAnyPayment.add(p.userId));
    return users.filter(user => !usersWithAnyPayment.has(user.id));
  }, [payments, users, refreshTrigger]);

  // ---------- Online QR Payment ----------
  const handleCreateQrPayment = async () => {
    if (!selectedUser || !amount || parseFloat(amount) <= 0) {
      alert("Please select a user and enter a valid amount");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/payments", {
        userId: selectedUser.id,
        amount: parseFloat(amount),
        description: description.trim(),
        paymentMethod: "ONLINE_QR",
      });
      setQrPayment(res.data);
      setShowQrModal(false);
      setSelectedUser(null);
      setAmount("");
      setDescription("");
      await refreshData();
    } catch (err) {
      alert("Failed to create payment: " + (err.response?.data?.message || err.message));
    } finally {
      setCreating(false);
    }
  };

  // ---------- Offline Payment (Cash/Cheque) ----------
  const handleCreateOfflinePayment = async () => {
    if (!offlineUser || !offlineAmount || parseFloat(offlineAmount) <= 0) {
      alert("Please select a user and enter a valid amount");
      return;
    }
    setOfflineCreating(true);
    try {
      const res = await api.post("/payments", {
        userId: offlineUser.id,
        amount: parseFloat(offlineAmount),
        description: offlineDescription.trim() || `Offline payment (${offlinePaymentMethod})`,
        paymentMethod: offlinePaymentMethod,
      });
      const paymentId = res.data.id;
      await api.put(`/payments/${paymentId}/pay`);
      alert(`✅ Offline payment of ₹${offlineAmount} recorded and marked as PAID.`);
      setShowOfflineModal(false);
      setOfflineUser(null);
      setOfflineAmount("");
      setOfflineDescription("");
      setOfflinePaymentMethod("CASH");
      await refreshData();
    } catch (err) {
      alert("Failed to record offline payment: " + (err.response?.data?.message || err.message));
    } finally {
      setOfflineCreating(false);
    }
  };

  const handleMarkPaid = async (paymentId) => {
    if (!window.confirm("Mark this payment as paid? User will be notified.")) return;
    try {
      await api.put(`/payments/${paymentId}/pay`);
      alert("✅ Payment marked as paid. Notification sent.");
      await refreshData();
    } catch (err) {
      alert("Error: " + (err.response?.data?.message || err.message));
    }
  };

  const handleNotify = async (paymentId) => {
    try {
      await api.post(`/payments/${paymentId}/notify`);
      alert("📧 Notification resent.");
    } catch (err) {
      alert("Failed to send notification: " + (err.response?.data?.message || err.message));
    }
  };

  const closeQrModal = () => setQrPayment(null);

  if (loading) {
    return (
      <div style={{ display: "flex" }}>
        <Sidebar />
        <div style={{ padding: "40px" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="payment-app">
      <Sidebar />
      <div className="payment-main">
        <div className="payment-header">
          <div>
            <h1>Payment Management</h1>
            <p>One‑time payment per user. Online (QR) or Offline (Cash/Cheque).</p>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button 
              className="btn-primary" 
              onClick={async () => {
                await refreshData();
                setShowQrModal(true);
              }}
            >
              + Online Payment (QR)
            </button>
            <button 
              className="btn-secondary" 
              onClick={async () => {
                await refreshData();
                setShowOfflineModal(true);
              }}
            >
              + Offline Payment (Cash/Cheque)
            </button>
          </div>
        </div>

        <div className="payments-list">
          <h2>All Payments</h2>
          {payments.length === 0 ? (
            <div className="no-data">No payments yet.</div>
          ) : (
            <table className="payments-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User (Coordinator)</th>
                  <th>Amount (₹)</th>
                  <th>Description</th>
                  <th>Payment Method</th>
                  <th>Created By (Admin)</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>
                      <strong>{p.userName}</strong>
                      <br />
                      <small>{p.userEmail}</small>
                    </td>
                    <td>₹{p.amount}</td>
                    <td>{p.description || "-"}</td>
                    <td>
                      {p.paymentMethod === "CASH" && "💵 Cash"}
                      {p.paymentMethod === "CHEQUE" && "📝 Cheque"}
                      {(!p.paymentMethod || p.paymentMethod === "ONLINE_QR") && "📱 Online QR"}
                    </td>
                    <td>{p.createdByName || (p.createdBy ? `Admin #${p.createdBy}` : "System")}</td>
                    <td>
                      <span className={`status-badge ${p.status === "PAID" ? "paid" : "pending"}`}>
                        {p.status === "PAID" ? "Paid" : "Pending"}
                      </span>
                    </td>
                    <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                    <td>
                      {p.status !== "PAID" && (
                        <>
                          <button className="btn-sm btn-paid" onClick={() => handleMarkPaid(p.id)}>
                            Mark Paid
                          </button>
                          <button className="btn-sm btn-notify" onClick={() => handleNotify(p.id)}>
                            Notify
                          </button>
                        </>
                      )}
                      {p.status === "PAID" && <span className="text-success">✓ Completed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal for Online (QR) Payment */}
      {showQrModal && (
        <div className="modal-overlay" onClick={() => setShowQrModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Online Payment (QR)</h3>
              <button className="modal-close" onClick={() => setShowQrModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label>Select User</label>
                  <button 
                    className="btn-sm" 
                    onClick={async () => {
                      await refreshData();
                      alert("User list refreshed");
                    }}
                    style={{ padding: "2px 8px", fontSize: "12px" }}
                  >
                    🔄 Refresh
                  </button>
                </div>
                <select
                  value={selectedUser?.id || ""}
                  onChange={e => {
                    const userId = parseInt(e.target.value);
                    const user = eligibleUsers.find(u => u.id === userId);
                    setSelectedUser(user);
                  }}
                >
                  <option value="">-- Choose a coordinator --</option>
                  {eligibleUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
                {eligibleUsers.length === 0 && (
                  <small style={{ color: "orange" }}>⚠️ No users available – every user already has a payment record.</small>
                )}
              </div>
              <div className="form-group">
                <label>Amount (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="Enter amount"
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows="2"
                  placeholder="Reason for payment"
                />
              </div>
              <p className="qr-note">✅ A QR code will be generated. User can scan and pay online.</p>
              <p className="qr-note" style={{ color: "#d9534f" }}>
                ⚠️ After creating this payment, the user will <strong>never appear</strong> in this dropdown again.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowQrModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateQrPayment} disabled={creating}>
                {creating ? "Creating..." : "Create & Generate QR"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Offline Payment (Cash/Cheque) */}
      {showOfflineModal && (
        <div className="modal-overlay" onClick={() => setShowOfflineModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Record Offline Payment</h3>
              <button className="modal-close" onClick={() => setShowOfflineModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label>Select User</label>
                  <button 
                    className="btn-sm" 
                    onClick={async () => {
                      await refreshData();
                      alert("User list refreshed");
                    }}
                    style={{ padding: "2px 8px", fontSize: "12px" }}
                  >
                    🔄 Refresh
                  </button>
                </div>
                <select
                  value={offlineUser?.id || ""}
                  onChange={e => {
                    const userId = parseInt(e.target.value);
                    const user = eligibleUsers.find(u => u.id === userId);
                    setOfflineUser(user);
                  }}
                >
                  <option value="">-- Choose a coordinator --</option>
                  {eligibleUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
                {eligibleUsers.length === 0 && (
                  <small style={{ color: "orange" }}>⚠️ No users available – every user already has a payment record.</small>
                )}
              </div>
              <div className="form-group">
                <label>Amount Received (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={offlineAmount}
                  onChange={e => setOfflineAmount(e.target.value)}
                  placeholder="Enter amount"
                />
              </div>
              <div className="form-group">
                <label>Description / Remarks</label>
                <textarea
                  value={offlineDescription}
                  onChange={e => setOfflineDescription(e.target.value)}
                  rows="2"
                  placeholder="e.g., Cash payment for training, Cheque number, etc."
                />
              </div>
              <div className="form-group">
                <label>Payment Method</label>
                <select 
                  value={offlinePaymentMethod} 
                  onChange={e => setOfflinePaymentMethod(e.target.value)}
                >
                  <option value="CASH">💵 Cash</option>
                  <option value="CHEQUE">📝 Cheque</option>
                </select>
              </div>
              <p className="qr-note">💵 The payment will be marked as <strong>PAID immediately</strong> (no QR, no pending step).</p>
              <p className="qr-note" style={{ color: "#d9534f" }}>
                ⚠️ After recording this payment, the user will <strong>never appear</strong> in this dropdown again.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowOfflineModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateOfflinePayment} disabled={offlineCreating}>
                {offlineCreating ? "Recording..." : "Record as Paid"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {qrPayment && (
        <div className="modal-overlay" onClick={closeQrModal}>
          <div className="modal-container qr-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment QR Code</h3>
              <button className="modal-close" onClick={closeQrModal}>×</button>
            </div>
            <div className="modal-body qr-body">
              <p><strong>User:</strong> {qrPayment.userName}</p>
              <p><strong>Amount:</strong> ₹{qrPayment.amount}</p>
              <p><strong>Payment ID:</strong> {qrPayment.id}</p>
              <div className="qr-code">
                <QRCodeCanvas
                  value={`upi://pay?pa=7002694744@ybl&pn=RHCWF&am=${qrPayment.amount}&cu=INR&txn=${qrPayment.id}&tn=${encodeURIComponent(qrPayment.description || 'Payment')}`}
                  size={200}
                  level="H"
                />
              </div>
              <p className="qr-instruction">Scan with any UPI app to pay</p>
              <p className="qr-note">
                After successful online payment, the admin must click <strong>"Mark Paid"</strong> in the list above.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={closeQrModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}